import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API } from "../../services/api";
import MailMessageContent from "../../components/MailMessageContent";
import OrdonnanceModal from "../../components/ordonnance/OrdonnanceModal";
import { isPharmacyAuthoredMessage } from "../../utils/commandeMail";

const pageSize = 50;

const S = {
    page: {
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        background: "#f8fafc",
        padding: "18px",
    },
    shell: {
        background: "white",
        border: "1px solid #dbe4f0",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
    },
    header: {
        padding: "18px 22px",
        borderBottom: "1px solid #e2e8f0",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
    },
    title: {
        margin: 0,
        color: "#0f172a",
        fontSize: "22px",
        fontWeight: 800,
    },
    subtitle: {
        marginTop: "4px",
        color: "#64748b",
        fontSize: "13px",
    },
    refresh: {
        border: "1px solid #cbd5e1",
        background: "white",
        borderRadius: "8px",
        padding: "8px 12px",
        color: "#334155",
        fontWeight: 700,
        cursor: "pointer",
    },
    filters: {
        padding: "14px 22px",
        borderBottom: "1px solid #e2e8f0",
        display: "flex",
        alignItems: "flex-end",
        gap: "12px",
        flexWrap: "wrap",
        background: "#f8fafc",
    },
    filterLabel: {
        display: "grid",
        gap: "5px",
        color: "#64748b",
        fontSize: "11px",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
    },
    select: {
        minWidth: "210px",
        border: "1px solid #cbd5e1",
        borderRadius: "8px",
        padding: "8px 10px",
        background: "white",
        color: "#334155",
        fontSize: "13px",
        fontWeight: 600,
        outline: "none",
        cursor: "pointer",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        tableLayout: "fixed",
    },
    th: {
        textAlign: "left",
        padding: "12px 16px",
        background: "#f1f5f9",
        color: "#64748b",
        fontSize: "12px",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        borderBottom: "1px solid #dbe4f0",
    },
    td: {
        verticalAlign: "top",
        padding: "14px 16px",
        borderBottom: "1px solid #e2e8f0",
    },
    mailButton: {
        width: "100%",
        border: "none",
        background: "transparent",
        padding: 0,
        textAlign: "left",
        cursor: "pointer",
    },
    ehpad: {
        color: "#0f172a",
        fontSize: "16px",
        fontWeight: 800,
    },
    meta: {
        marginTop: "4px",
        color: "#64748b",
        fontSize: "13px",
        lineHeight: 1.45,
    },
    badgeLine: {
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        marginTop: "10px",
    },
    badge: {
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "999px",
        padding: "3px 8px",
        background: "#eef2ff",
        color: "#3730a3",
        fontSize: "12px",
        fontWeight: 700,
    },
    textarea: {
        width: "100%",
        minHeight: "92px",
        boxSizing: "border-box",
        resize: "vertical",
        border: "1px solid #cbd5e1",
        borderRadius: "10px",
        padding: "10px 12px",
        color: "#1e293b",
        fontSize: "14px",
        lineHeight: 1.45,
        outline: "none",
    },
    treatedCell: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "8px",
        color: "#475569",
        fontSize: "13px",
    },
    checkboxLabel: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        color: "#0f172a",
        fontWeight: 800,
        cursor: "pointer",
    },
    modalBackdrop: {
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
    },
    modal: {
        width: "min(900px, 94vw)",
        maxHeight: "86vh",
        background: "white",
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)",
        display: "flex",
        flexDirection: "column",
    },
    modalSectionTitle: {
        color: "#0f172a",
        fontSize: "14px",
        fontWeight: 800,
        marginBottom: "10px",
    },
    patientBlock: {
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        overflow: "hidden",
        marginBottom: "10px",
        background: "white",
    },
    patientHeader: {
        padding: "10px 12px",
        background: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        color: "#0f172a",
        fontWeight: 800,
        fontSize: "14px",
    },
    productRow: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 72px 110px",
        gap: "10px",
        padding: "9px 12px",
        borderTop: "1px solid #f1f5f9",
        alignItems: "center",
        color: "#334155",
        fontSize: "13px",
    },
};

function formatDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
    }) + " · " + date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
    });
}

function patientLabel(patient) {
    return [patient?.prenom, patient?.nom].filter(Boolean).join(" ").trim();
}

function productLabel(product) {
    return [product?.nom, product?.dosage].filter(Boolean).join(" ").trim() || "Produit non renseigné";
}

function PatientProductsDetail({ patients }) {
    if (!patients?.length) {
        return (
            <div style={{ color: "#94a3b8", fontSize: "13px" }}>
                Aucun patient renseigné sur cette réclamation.
            </div>
        );
    }

    return patients.map((patient, patientIndex) => {
        const products = patient.produits || [];
        return (
            <div key={patient.commandePatientId ?? patient.id ?? patientIndex} style={S.patientBlock}>
                <div style={S.patientHeader}>
                    {patientLabel(patient) || "Patient non renseigné"}
                    {patient.classification && (
                        <span style={{ marginLeft: "8px", color: "#64748b", fontSize: "12px", fontWeight: 700 }}>
                            {patient.classification}
                        </span>
                    )}
                </div>
                {products.length === 0 ? (
                    <div style={{ padding: "10px 12px", color: "#94a3b8", fontSize: "13px" }}>
                        Aucun produit renseigné.
                    </div>
                ) : products.map((product, productIndex) => (
                    <div key={product.id ?? productIndex} style={S.productRow}>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 800, color: "#1e293b", overflowWrap: "anywhere" }}>
                                {productLabel(product)}
                            </div>
                            {product.posologie && (
                                <div style={{ color: "#64748b", marginTop: "2px", overflowWrap: "anywhere" }}>
                                    {product.posologie}
                                </div>
                            )}
                        </div>
                        <div style={{ color: "#64748b", fontWeight: 700 }}>
                            Qté {product.quantite ?? 1}
                        </div>
                        <div style={{ color: "#475569", fontWeight: 800 }}>
                            {product.destination || product.statut || "Non orienté"}
                        </div>
                    </div>
                ))}
            </div>
        );
    });
}

function ReclamationMailModal({ commandeId, onClose }) {
    const [detail, setDetail] = useState(null);
    const [selectedOrdonnance, setSelectedOrdonnance] = useState(null);

    useEffect(() => {
        setDetail(null);
        API.get(`/commandes/${commandeId}`, { noRedirectOn401: true })
            .then(response => setDetail(response.data))
            .catch(console.error);
    }, [commandeId]);

    const files = detail
        ? (detail.fileNames || []).map((name, index) => ({ name, id: (detail.ordonnanceIds || [])[index] ?? null }))
        : [];

    return (
        <div style={{ ...S.modalBackdrop, background: "transparent" }}>
            <button type="button" aria-label="Fermer le détail de la réclamation" onClick={onClose} style={{ position: "absolute", inset: 0, border: 0, background: S.modalBackdrop.background }} />
            <dialog open aria-label="Détail de la réclamation" onKeyDown={event => { if (event.key === "Escape") onClose(); }} style={{ ...S.modal, position: "relative", margin: 0, padding: 0, border: 0 }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", gap: "12px" }}>
                    <div>
                        <div style={{ color: "#0f172a", fontSize: "17px", fontWeight: 800 }}>
                            {detail?.commentaire || "Réclamation"}
                        </div>
                        <div style={{ marginTop: "3px", color: "#64748b", fontSize: "13px" }}>
                            {detail?.ehpad?.nom || "EHPAD non renseigné"} · Commande #{commandeId}
                        </div>
                    </div>
                    <button type="button" onClick={onClose} style={S.refresh}>Fermer</button>
                </div>

                {!detail ? (
                    <div style={{ padding: "36px", color: "#94a3b8", textAlign: "center" }}>Chargement…</div>
                ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 220px", minHeight: 0, overflow: "hidden" }}>
                        <div style={{ padding: "16px 18px", overflowY: "auto", maxHeight: "68vh" }}>
                            <div style={S.modalSectionTitle}>Mail</div>
                            {(!detail.messages || detail.messages.length === 0) ? (
                                <div style={{ color: "#94a3b8", fontSize: "13px" }}>Aucun corps de mail disponible.</div>
                            ) : detail.messages.map((message, index) => (
                                <div key={message.id ?? index} style={{ marginBottom: "14px" }}>
                                    <div style={{ marginBottom: "5px", color: "#64748b", fontSize: "12px", fontWeight: 700 }}>
                                        {isPharmacyAuthoredMessage(message)
                                            ? "Réponse pharmacie"
                                            : detail.source === "PORTAIL_COLLECTIVITE"
                                                ? "Portail collectivité"
                                                : detail.emailEmetteur || "Mail reçu"}
                                        {message.dateMessage && <span> · {formatDateTime(message.dateMessage)}</span>}
                                    </div>
                                    <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", padding: "12px", color: "#334155", fontSize: "14px", lineHeight: 1.6 }}>
                                        <MailMessageContent html={message.contenuHtml} text={message.contenu} />
                                    </div>
                                </div>
                            ))}
                            <div style={{ ...S.modalSectionTitle, marginTop: "18px" }}>Patients et produits</div>
                            <PatientProductsDetail patients={detail.patients || []} />
                        </div>
                        <aside style={{ borderLeft: "1px solid #e2e8f0", padding: "16px", background: "#f8fafc" }}>
                            <div style={{ color: "#334155", fontSize: "13px", fontWeight: 800, marginBottom: "10px" }}>
                                Pièces jointes ({files.length})
                            </div>
                            {files.length === 0 ? (
                                <div style={{ color: "#94a3b8", fontSize: "13px" }}>Aucune pièce jointe.</div>
                            ) : files.map((file, index) => (
                                <button
                                    key={`${file.id || index}-${file.name}`}
                                    type="button"
                                    onClick={() => file.id && setSelectedOrdonnance(file)}
                                    style={{
                                        width: "100%",
                                        marginBottom: "8px",
                                        border: "1px solid #cbd5e1",
                                        borderRadius: "8px",
                                        background: "white",
                                        padding: "8px 10px",
                                        textAlign: "left",
                                        color: file.id ? "#2563eb" : "#64748b",
                                        cursor: file.id ? "pointer" : "default",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {file.name || "Fichier"}
                                </button>
                            ))}
                        </aside>
                    </div>
                )}

                {selectedOrdonnance && (
                    <OrdonnanceModal
                        ordonnance={selectedOrdonnance}
                        draft={null}
                        setDraft={() => {}}
                        onClose={() => setSelectedOrdonnance(null)}
                    />
                )}
            </dialog>
        </div>
    );
}

export default function Reclamation() {
    const [commandes, setCommandes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingIds, setSavingIds] = useState(new Set());
    const [selectedCommandeId, setSelectedCommandeId] = useState(null);
    const [ehpads, setEhpads] = useState([]);
    const [tournees, setTournees] = useState([]);
    const [ehpadId, setEhpadId] = useState("");
    const [tourneeFilter, setTourneeFilter] = useState("");
    const actionSaveTimersRef = useRef(new Map());
    const latestActionsRef = useRef(new Map());

    const patchCommande = useCallback(async (commandeId, patch) => {
        setSavingIds(current => new Set(current).add(commandeId));
        try {
            const response = await API.patch(`/commandes/${commandeId}/reclamation`, patch, { noRedirectOn401: true });
            setCommandes(current => current.map(commande =>
                commande.id === commandeId
                    ? {
                        ...commande,
                        ...(Object.prototype.hasOwnProperty.call(patch, "actionCorrective")
                            ? latestActionsRef.current.get(commandeId) === patch.actionCorrective
                                ? { reclamationActionCorrective: response.data?.reclamationActionCorrective ?? "" }
                                : {}
                            : {}),
                        reclamationTraitee: Boolean(response.data?.reclamationTraitee),
                        reclamationTraiteeBy: response.data?.reclamationTraiteeBy ?? null,
                        reclamationTraiteeAt: response.data?.reclamationTraiteeAt ?? null,
                    }
                    : commande
            ));
        } catch (error) {
            console.error(error);
            alert("Impossible d'enregistrer la réclamation.");
        } finally {
            setSavingIds(current => {
                const next = new Set(current);
                next.delete(commandeId);
                return next;
            });
        }
    }, []);

    const flushActionSave = useCallback((commandeId, value) => {
        const timer = actionSaveTimersRef.current.get(commandeId);
        if (timer) clearTimeout(timer);
        actionSaveTimersRef.current.delete(commandeId);
        latestActionsRef.current.set(commandeId, value);
        return patchCommande(commandeId, { actionCorrective: value });
    }, [patchCommande]);

    const flushPendingActionSaves = useCallback(async () => {
        const pendingIds = Array.from(actionSaveTimersRef.current.keys());
        await Promise.all(pendingIds.map(commandeId => {
            const value = latestActionsRef.current.get(commandeId) ?? "";
            return flushActionSave(commandeId, value);
        }));
    }, [flushActionSave]);

    const scheduleActionSave = useCallback((commandeId, value) => {
        latestActionsRef.current.set(commandeId, value);
        const previous = actionSaveTimersRef.current.get(commandeId);
        if (previous) clearTimeout(previous);
        const timer = setTimeout(() => {
            actionSaveTimersRef.current.delete(commandeId);
            void patchCommande(commandeId, { actionCorrective: value });
        }, 700);
        actionSaveTimersRef.current.set(commandeId, timer);
    }, [patchCommande]);

    const load = useCallback(() => {
        setLoading(true);
        flushPendingActionSaves()
            .then(() => API.get("/commandes", {
                params: {
                    destination: "RECLAMATION",
                    page: 0,
                    size: pageSize,
                    ...(ehpadId ? { ehpadId } : {}),
                    ...(tourneeFilter ? { tournee: tourneeFilter } : {}),
                },
                noRedirectOn401: true,
            }))
            .then(response => setCommandes(response.data?.content || []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [ehpadId, flushPendingActionSaves, tourneeFilter]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        API.get("/ehpads", { noRedirectOn401: true }).then(response => setEhpads(response.data || [])).catch(() => setEhpads([]));
        API.get("/ehpads/tournees", { noRedirectOn401: true }).then(response => setTournees(response.data || [])).catch(() => setTournees([]));
    }, []);

    useEffect(() => {
        const timers = actionSaveTimersRef.current;
        return () => {
            timers.forEach(timer => clearTimeout(timer));
            timers.clear();
        };
    }, []);

    const sortedCommandes = useMemo(() => {
        return [...commandes].sort((a, b) => new Date(b.dateReception || 0) - new Date(a.dateReception || 0));
    }, [commandes]);

    const updateLocalAction = (commandeId, value) => {
        scheduleActionSave(commandeId, value);
        setCommandes(current => current.map(commande =>
            commande.id === commandeId ? { ...commande, reclamationActionCorrective: value } : commande
        ));
    };

    return (
        <div style={S.page}>
            <div style={S.shell}>
                <div style={S.header}>
                    <div>
                        <h1 style={S.title}>Réclamations</h1>
                        <div style={S.subtitle}>
                            {loading ? "Chargement…" : `${sortedCommandes.length} demande${sortedCommandes.length > 1 ? "s" : ""}`}
                        </div>
                    </div>
                    <button type="button" onClick={load} style={S.refresh}>Actualiser</button>
                </div>

                <div style={S.filters}>
                    <label style={S.filterLabel}>
                        <span>EHPAD</span>
                        <select
                            value={ehpadId}
                            onChange={event => setEhpadId(event.target.value)}
                            style={{
                                ...S.select,
                                borderColor: ehpadId ? "#93c5fd" : "#cbd5e1",
                                background: ehpadId ? "#eff6ff" : "white",
                            }}
                        >
                            <option value="">Tous les EHPAD</option>
                            {ehpads.map(ehpad => (
                                <option key={ehpad.id} value={ehpad.id}>{ehpad.nom || ehpad.code}</option>
                            ))}
                        </select>
                    </label>
                    <label style={S.filterLabel}>
                        <span>Tournée</span>
                        <select
                            value={tourneeFilter}
                            onChange={event => setTourneeFilter(event.target.value)}
                            style={{
                                ...S.select,
                                borderColor: tourneeFilter ? "#93c5fd" : "#cbd5e1",
                                background: tourneeFilter ? "#eff6ff" : "white",
                            }}
                        >
                            <option value="">Toutes les tournées</option>
                            {tournees.map(tournee => (
                                <option key={tournee.id || tournee.name} value={tournee.name}>{tournee.name}</option>
                            ))}
                        </select>
                    </label>
                    {(ehpadId || tourneeFilter) && (
                        <button
                            type="button"
                            onClick={() => {
                                setEhpadId("");
                                setTourneeFilter("");
                            }}
                            style={{ ...S.refresh, padding: "8px 11px" }}
                        >
                            Réinitialiser
                        </button>
                    )}
                </div>

                <table style={S.table}>
                    <colgroup>
                        <col style={{ width: "42%" }} />
                        <col style={{ width: "38%" }} />
                        <col style={{ width: "20%" }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th style={S.th}>Infos du mail</th>
                            <th style={S.th}>Actions correctives</th>
                            <th style={S.th}>Demande traitée ?</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedCommandes.map(commande => {
                            const patients = commande.patients || [];
                            const saving = savingIds.has(commande.id);
                            return (
                                <tr key={commande.id}>
                                    <td style={S.td}>
                                        <button type="button" style={S.mailButton} onClick={() => setSelectedCommandeId(commande.id)}>
                                            <div style={S.ehpad}>{commande.ehpadNom || "EHPAD non renseigné"}</div>
                                            <div style={S.meta}>
                                                {formatDateTime(commande.dateReception) || "Date non renseignée"} · Commande #{commande.id}
                                            </div>
                                            {commande.commentaire && (
                                                <div style={{ ...S.meta, color: "#334155", fontWeight: 700 }}>{commande.commentaire}</div>
                                            )}
                                            <div style={S.badgeLine}>
                                                {patients.length === 0 ? (
                                                    <span style={S.badge}>Aucun patient</span>
                                                ) : patients.map((patient, index) => (
                                                    <span key={patient.commandePatientId ?? index} style={S.badge}>
                                                        {patientLabel(patient) || "Patient non renseigné"}
                                                    </span>
                                                ))}
                                            </div>
                                            {(commande.fileNames || []).length > 0 && (
                                                <div style={{ ...S.meta, marginTop: "8px" }}>
                                                    {(commande.fileNames || []).length} pièce{commande.fileNames.length > 1 ? "s" : ""} jointe{commande.fileNames.length > 1 ? "s" : ""}
                                                </div>
                                            )}
                                        </button>
                                    </td>
                                    <td style={S.td}>
                                        <textarea
                                            aria-label={`Actions correctives commande ${commande.id}`}
                                            value={commande.reclamationActionCorrective || ""}
                                            onChange={event => updateLocalAction(commande.id, event.target.value)}
                                            onBlur={event => flushActionSave(commande.id, event.target.value)}
                                            placeholder="Commentaire de l'opérateur"
                                            style={S.textarea}
                                        />
                                        {saving && <div style={{ marginTop: "6px", color: "#64748b", fontSize: "12px" }}>Enregistrement…</div>}
                                    </td>
                                    <td style={S.td}>
                                        <div style={S.treatedCell}>
                                            <label style={S.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(commande.reclamationTraitee)}
                                                    onChange={event => patchCommande(commande.id, { traitee: event.target.checked })}
                                                />
                                                <span>Traité</span>
                                            </label>
                                            {commande.reclamationTraitee && (
                                                <div>
                                                    <div>{commande.reclamationTraiteeBy || "Utilisateur inconnu"}</div>
                                                    <div style={{ color: "#64748b" }}>{formatDateTime(commande.reclamationTraiteeAt)}</div>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {!loading && sortedCommandes.length === 0 && (
                            <tr>
                                <td colSpan={3} style={{ padding: "36px", textAlign: "center", color: "#94a3b8" }}>
                                    Aucune réclamation.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {selectedCommandeId && (
                <ReclamationMailModal
                    commandeId={selectedCommandeId}
                    onClose={() => setSelectedCommandeId(null)}
                />
            )}
        </div>
    );
}
